import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './useAuth';

export interface BetaAccessInfo {
  hasActiveBeta: boolean;
  daysRemaining: number | null;
  expiresAt: string | null;
  discountPercentage: number | null;
  discountMonths: number | null;
  batchIdentifier: string | null;
  accessCode: string | null;
  accessType: string | null;
  monthlyDiscountCoupon: string | null;
  annualDiscountCoupon: string | null;
}

export function useBetaAccess() {
  const { user } = useAuth();
  const [betaAccess, setBetaAccess] = useState<BetaAccessInfo>({
    hasActiveBeta: false,
    daysRemaining: null,
    expiresAt: null,
    discountPercentage: null,
    discountMonths: null,
    batchIdentifier: null,
    accessCode: null,
    accessType: null,
    monthlyDiscountCoupon: null,
    annualDiscountCoupon: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBetaAccess = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get account for the current user
        const { data: account, error: accountError } = await supabase
          .from('accounts')
          .select('id, is_beta_tester')
          .eq('owner_id', user.id)
          .single();

        if (accountError || !account || !account.is_beta_tester) {
          setBetaAccess({
            hasActiveBeta: false,
            daysRemaining: null,
            expiresAt: null,
            discountPercentage: null,
            discountMonths: null,
            batchIdentifier: null,
            accessCode: null,
            accessType: null,
            monthlyDiscountCoupon: null,
            annualDiscountCoupon: null,
          });
          setLoading(false);
          return;
        }

        // Get active beta grant
        const { data: grant, error: grantError } = await supabase
          .from('account_access_grants')
          .select('*')
          .eq('account_id', account.id)
          .eq('status', 'active')
          .single();

        if (grantError || !grant) {
          setBetaAccess({
            hasActiveBeta: false,
            daysRemaining: null,
            expiresAt: null,
            discountPercentage: null,
            discountMonths: null,
            batchIdentifier: null,
            accessCode: null,
            accessType: null,
            monthlyDiscountCoupon: null,
            annualDiscountCoupon: null,
          });
          setLoading(false);
          return;
        }

        // Calculate days remaining
        const expiresAt = new Date(grant.expires_at);
        const now = new Date();
        const daysRemaining = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        // Get the access code details to fetch discount percentage and coupons
        let discountPercentage = null; // Don't assume a default
        let discountMonths = null;
        let batchIdentifier = null;
        let monthlyDiscountCoupon = null;
        let annualDiscountCoupon = null;
        let accessType = null;
        
        try {
          const { data: accessCode } = await supabase
            .from('access_codes')
            .select('*')
            .eq('code', grant.access_code_used)
            .single();
          
          if (accessCode) {
            batchIdentifier = accessCode.batch_identifier;
            monthlyDiscountCoupon = accessCode.monthly_discount_coupon_id;
            annualDiscountCoupon = accessCode.annual_discount_coupon_id;
            accessType = accessCode.type; // Get the actual type from database
            
            // Debug logging
            console.log('Access code details:', {
              code: grant.access_code_used,
              type: accessType,
              monthlyDiscountCoupon,
              annualDiscountCoupon,
              metadata: accessCode.metadata
            });
            
            // Fetch actual Stripe coupon details if coupons exist
            if (monthlyDiscountCoupon || annualDiscountCoupon) {
              try {
                console.log('Fetching Stripe coupon details for:', { monthlyDiscountCoupon, annualDiscountCoupon });
                const response = await fetch('/api/get-coupon-details', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    monthlyDiscountCouponId: monthlyDiscountCoupon,
                    annualDiscountCouponId: annualDiscountCoupon
                  })
                });
                
                if (response.ok) {
                  const couponData = await response.json();
                  console.log('Coupon data received:', couponData);
                  
                  // Check for error message in response
                  if (couponData.error) {
                    console.error('Stripe coupon fetch error:', couponData.error);
                  }
                  
                  if (couponData.discountPercentage) {
                    discountPercentage = couponData.discountPercentage;
                    discountMonths = couponData.discountMonths;
                    
                    // Handle "forever" discounts more gracefully in display
                    if (discountMonths === 999) {
                      discountMonths = null; // Will display as "your subscription" without "for up to X months"
                    }
                  }
                } else {
                  console.error('Failed to fetch coupon details, status:', response.status);
                }
              } catch (error) {
                console.log('Could not fetch Stripe coupon details:', error);
                // Fall back to metadata if available
                if (accessCode.metadata?.discount_percentage) {
                  discountPercentage = accessCode.metadata.discount_percentage;
                  discountMonths = accessCode.metadata.discount_months;
                }
              }
            } else if (accessCode.metadata?.discount_percentage) {
              // Use metadata if no Stripe coupons
              console.log('Using metadata for discount:', accessCode.metadata);
              discountPercentage = accessCode.metadata.discount_percentage;
              discountMonths = accessCode.metadata.discount_months;
            } else {
              console.log('No discount information found for access code');
            }
          }
        } catch (error) {
          console.log('Could not fetch access code discount details');
        }

        setBetaAccess({
          hasActiveBeta: daysRemaining > 0,
          daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
          expiresAt: grant.expires_at,
          discountPercentage,
          discountMonths,
          batchIdentifier,
          accessCode: grant.access_code_used,
          accessType,
          monthlyDiscountCoupon,
          annualDiscountCoupon,
        });
      } catch (error) {
        console.error('Error fetching beta access:', error);
        setBetaAccess({
          hasActiveBeta: false,
          daysRemaining: null,
          expiresAt: null,
          discountPercentage: null,
          batchIdentifier: null,
          accessCode: null,
          monthlyDiscountCoupon: null,
          annualDiscountCoupon: null,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBetaAccess();

    // Subscribe to grant changes
    const channel = supabase
      .channel('beta-access')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_access_grants',
        },
        () => {
          fetchBetaAccess();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return { betaAccess, loading };
}